import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Loader2, Crosshair, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { reverseGeocode } from "@/lib/reverseGeocode";
import { toast } from "sonner";
import type { QuoteWizardState } from "../DoorKnockerQuickQuote";

interface Props {
  state: QuoteWizardState;
  setState: React.Dispatch<React.SetStateAction<QuoteWizardState>>;
  onNext: () => void;
}

export function StepLocation({ state, setState, onNext }: Props) {
  const [locating, setLocating] = useState(false);

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("GPS not available on this device");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const addr = await reverseGeocode(latitude, longitude);
          setState((s) => ({ ...s, address: addr, lat: latitude, lng: longitude }));
          toast.success("Location captured");
        } catch (e) {
          toast.error("Couldn't read address. Type it in below.");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        toast.error(err.message || "Location denied");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  return (
    <div className="px-5 pt-6 pb-8 space-y-6">
      <div>
        <div className="text-xs font-semibold text-primary uppercase tracking-wider mb-1.5">Step 1 / 4</div>
        <h2 className="text-2xl font-bold tracking-tight">Where are you?</h2>
        <p className="text-sm text-muted-foreground mt-1">
          We'll measure the roof from satellite in seconds.
        </p>
      </div>

      <button
        onClick={useMyLocation}
        disabled={locating}
        className="relative w-full h-32 rounded-2xl bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground p-5 text-left active:scale-[0.98] transition-transform overflow-hidden disabled:opacity-70"
      >
        <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute right-4 top-4">
          {locating ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <Crosshair className="h-6 w-6" />
          )}
        </div>
        <div className="text-lg font-bold">Use My Location</div>
        <div className="text-xs opacity-90 mt-1">
          {locating ? "Getting GPS fix…" : "Auto-fill the property address from GPS"}
        </div>
      </button>

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-[11px] text-muted-foreground font-medium uppercase tracking-wider">or type it</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5" /> Property Address
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={state.address}
            onChange={(e) => setState((s) => ({ ...s, address: e.target.value }))}
            placeholder="123 Main St, Springfield, IL"
            className="pl-9 h-12 text-base"
          />
        </div>
      </div>

      <Button
        onClick={onNext}
        disabled={!state.address.trim()}
        className="w-full h-12 text-base font-semibold"
        size="lg"
      >
        Continue
      </Button>
    </div>
  );
}
